import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TestCaseResultsPanel } from './TestCaseResultsPanel';
import { ResultsPanelClosed } from '../../ResultsPanelClosed/ResultsPanelClosed';
import { settingSelectors, updateSplit3Size } from '../../../../store/setting';
import SplitPane from 'react-split-pane-r17';
import { TestCaseInputPanel } from './TestCaseInputPanel';
import { trackEvent } from '../../../../services/analytics';
import { Test } from '../../../../types/RealtaDocument';
import { ToolbarIconButton } from '../../../ui/ToolbarIconButton/ToolbarIconButton';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@mui/material';
import { documentService } from '../../../../services/DocumentService';
import { addTest, testSelectors } from '../../../../store/test';
import useRole from '../../../../utils/use-role';
import useUsername from '../../../../utils/use-username';

type TestCasePanelProps = {
  tests: Test[];
  sectionId: string;
  onTestsChange?: () => void;
  onSave?: (test: Test) => void;
};

const TestCasePanel: React.FC<TestCasePanelProps> = ({ tests, sectionId, onTestsChange, onSave }) => {
  const dispatch = useDispatch();
  const { isReadOnly } = useRole();
  const username = useUsername();

  const isResultsPanelClosed = useSelector(settingSelectors.isResultsPanelClosed);
  const split3Size = useSelector(settingSelectors.split3Size);
  const selectedTestId = useSelector(testSelectors.selectedTestId);

  const split3Props: any = {
    primary: isResultsPanelClosed ? 'second' : 'first',
    allowResize: !isResultsPanelClosed,
    size: isResultsPanelClosed ? '35px' : split3Size || '50%',
    pane1Style:
      (!isResultsPanelClosed && {
        minWidth: '35%',
        maxWidth: '74%',
      }) ||
      {},
    pane2Style:
      (!isResultsPanelClosed && {
        minWidth: '25%',
        maxWidth: '65%',
      }) ||
      {},
  };

  const handleSplit3Drag = (size: number) => dispatch(updateSplit3Size(size));

  const handleSplit3DoubleClick = () => {
    !isResultsPanelClosed && dispatch(updateSplit3Size('50%'));
    trackEvent.splitPaneDoubleClick();
  };

  const handleCreateNew = async () => {
    const newTest = await documentService.createTest({
      name: 'Untitled test case',
      description: '',
      parentNode: sectionId,
      createdBy: username,
    });
    newTest && dispatch(addTest(newTest));
  };

  return tests.length > 0 ? (
    selectedTestId ? (
      <SplitPane
        split="vertical"
        {...split3Props}
        onDragFinished={handleSplit3Drag}
        onResizerDoubleClick={handleSplit3DoubleClick}
        hoverDisabled={isResultsPanelClosed}
      >
        <TestCaseInputPanel onSave={onSave} onTestsChange={onTestsChange} />
        {isResultsPanelClosed ? <ResultsPanelClosed /> : <TestCaseResultsPanel />}
      </SplitPane>
    ) : (
      <Box textAlign="center" pt={5}>
        Select a test case
      </Box>
    )
  ) : (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '100px', lineHeight: '2' }}>
      <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 500 }}>
        Create new test case.
      </Box>
      <ToolbarIconButton aria-label="Create new" title="Create new" onClick={handleCreateNew} disabled={isReadOnly}>
        <AddIcon fontSize="large" />
      </ToolbarIconButton>
    </Box>
  );
};

export { TestCasePanel };
